'use client';

import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { randomFuncName } from '@/utils/tools';
import { useRouter } from 'next/navigation';
import { useCallback, useContext } from 'react';

/**
 * App导航Hook
 *
 * 根据当前是否在App内嵌环境，自动选择合适的导航方式：
 * - App内：使用appCommunicationManager发送消息给原生App
 * - Web端：使用Next.js router进行页面跳转
 *
 * @returns navigate - 统一的导航方法
 */
export function useAppNavigation() {
  const router = useRouter();
  const { deviceType } = useContext(ResponsiveContext);
  const inAppH5 = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
  // App相关状态
  const setToken = useStore((state) => state.setToken);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);

  const navigate = useCallback(
    (url: string) => {
      if (inAppH5) {
        // 在App内跳转前，设置回调函数用于从App返回时重新获取token
        try {
          // 生成随机回调函数名
          const callbackFnName = randomFuncName('AppNavigation');

          // 注册回调函数到window对象
          (window as any)[callbackFnName] = async (val: any) => {
            logger.log('appNavigation+++++++++');
            if (val !== 'ClearToken') {
              const token = localStorage?.getItem('zl-token');
              if (val !== token) {
                setToken(val);
                await refreshAccountInfo();
              }
            }
          };

          const data = {
            methodName: 'backRemind', // 方法名称必传字段，backRemind-需要页面返回调用方法告诉前端回到web页面
            Callback: callbackFnName, // 使用生成的回调函数名，用于跳转至app之后，app返回web页面，重新调用获取token的方法
          };

          // 直接调用window.OtherMethod.postMessage，确保消息能正确发送
          if ((window as any).OtherMethod) {
            (window as any).OtherMethod.postMessage(JSON.stringify(data));
          } else {
            appCommunicationManager.sendMessage(EAppCommunicationFuncName.OTHER_METHOD, data);
          }
        } catch {
          // console.error('OtherMethod postMessage failed:', error);
        }

        // App内跳转逻辑
        if (url.includes('/goods-detail/')) {
          const shop_sku = url.split('/goods-detail/')[1];
          appCommunicationManager.sendMessage(
            EAppCommunicationFuncName.JUMP_GOODS_DETAIL,
            shop_sku,
          );
        } else if (url.includes('/goods-list/')) {
          const groupId = url.split('/goods-list/')[1];
          appCommunicationManager.sendMessage(
            EAppCommunicationFuncName.JUMP_GOODS_LIST,
            `${groupId}`,
          );
        } else {
          // 其他页面跳转
          appCommunicationManager.sendMessage(EAppCommunicationFuncName.JUMP_TO_PAGE, {
            RouteName: url,
          });
        }
        return;
      }
      // 非App环境使用普通路由跳转
      router.push(url);
    },
    [inAppH5, router],
  );

  return { navigate, inAppH5 };
}
